"use client";

import { useCallback } from "react";
import { useSearchParams } from "next/navigation";
import useRouterParams from "./useRouterParams";
import {
  MultiFilterOperator,
  parseMultiParameter,
  serializeMultiParameter,
} from "./multiParameter";

/**
 * Multi-value URL param + sibling operator param backed by
 * `useRouterParams.setParams`. The values param holds an optionally
 * operator-prefixed string (e.g. `none:1,2`) and the operator param is a
 * Default-View-friendly fallback when no prefix is present. Writes resolve
 * the operator into the values prefix; the sibling operator param is only
 * written for non-default operators so URLs stay tidy.
 */
export default function useMultiValueParameter(
  valuesParameterName: string,
  operatorParameterName: string,
) {
  const searchParameters = useSearchParams();
  const { setParams } = useRouterParams();

  const rawValues = searchParameters?.get(valuesParameterName) ?? undefined;
  const rawOperator = searchParameters?.get(operatorParameterName) ?? undefined;

  const { values, operator } = parseMultiParameter(rawValues, rawOperator);

  const write = useCallback(
    (nextValues: string[], nextOperator: MultiFilterOperator) => {
      const serialized = serializeMultiParameter(nextOperator, nextValues);
      if (!serialized) {
        setParams({ page: 1 }, [valuesParameterName, operatorParameterName]);
        return;
      }
      if (nextOperator === "any") {
        // `any` is the default — clear the sibling operator param so the URL
        // matches the legacy `owners=1,2` shape.
        setParams({ [valuesParameterName]: serialized, page: 1 }, [
          operatorParameterName,
        ]);
        return;
      }
      setParams({
        [valuesParameterName]: serialized,
        page: 1,
        [operatorParameterName]: nextOperator,
      });
    },
    [operatorParameterName, setParams, valuesParameterName],
  );

  const toggle = useCallback(
    (id: string, pendingOperator?: MultiFilterOperator) => {
      const next = values.includes(id)
        ? values.filter((value) => value !== id)
        : [...values, id];
      write(next, pendingOperator ?? operator);
    },
    [operator, values, write],
  );

  const setOperator = useCallback(
    (nextOperator: MultiFilterOperator) => {
      // Operator alone is meaningless without values — don't write a URL
      // with a bare operator param and no values.
      if (values.length === 0) return;
      write(values, nextOperator);
    },
    [values, write],
  );

  const clear = useCallback(() => {
    setParams({ page: 1 }, [valuesParameterName, operatorParameterName]);
  }, [operatorParameterName, setParams, valuesParameterName]);

  return { values, operator, toggle, setOperator, clear };
}
